import { BarChart } from '@mui/x-charts/BarChart'
import { Box, Typography } from '@mui/material'
import { useXScale, useYScale, useDrawingArea } from '@mui/x-charts/hooks'
import type { CategoryRateDto } from "store/dashboard"
import { barChartSlotProps } from './chartDefaults'

interface Props {
  data: CategoryRateDto[]
}

const XAxisGrid = ({ data }: Props) => {
  const xScale = useXScale()
  const { top, height } = useDrawingArea()

  if (!('bandwidth' in xScale)) return null

  return (
    <>
      {data.map((d) => {
        const x = (xScale(d.category) ?? 0) + xScale.bandwidth()
        return <line key={d.category} x1={x} x2={x} y1={top} y2={top + height} stroke='#E0E0E0' strokeDasharray='4' />
      })}
    </>
  )
}

const BarValueLabels = ({ data }: Props) => {
  const xScale = useXScale()
  const yScale = useYScale()

  if (!('bandwidth' in xScale)) return null

  return (
    <>
      {data.map((d) => {
        const x = (xScale(d.category) ?? 0) + xScale.bandwidth() / 2
        const y = yScale(d.rate) ?? 0
        return (
          <text key={d.category} x={x} y={y - 8} textAnchor='middle' fontSize={12} fontWeight={700} fill='#002C5F'>
            {d.rate.toFixed(1)}%
          </text>
        )
      })}
    </>
  )
}

const CompletionRateChart = ({ data }: Props) => {
  if (!data || data.length === 0) return null

  const totalCompleted = data.reduce((acc, d) => acc + d.completed, 0)
  const totalBooking = data.reduce((acc, d) => acc + d.total, 0)

  return (
    <Box>
      <Typography variant='body2' align='center' sx={{ fontWeight: 700, color: '#002C5F', mb: 1 }}>
        {totalCompleted.toLocaleString('id-ID')} / {totalBooking.toLocaleString('id-ID')} Completed
      </Typography>
      <BarChart
        series={[
          { data: data.map(d => d.rate), label: 'Completion Rate', color: '#28A138' },
        ]}
        xAxis={[{
          data: data.map(d => d.category),
          scaleType: 'band',
          valueFormatter: (category) => {
            const item = data.find(d => d.category === category)
            return item ? `${category} (${item.completed}/${item.total})` : category
          }
        }]}
        yAxis={[{ min: 0, max: 100, valueFormatter: (v) => `${v}%` }]}
        height={400}
        slotProps={{ ...barChartSlotProps, bar: undefined }}
        sx={{ '& .MuiChartsLegend-mark': { rx: 4, ry: 4 } }}
      >
        <XAxisGrid data={data} />
        <BarValueLabels data={data} />
      </BarChart>
    </Box>
  )
}

export default CompletionRateChart
